import { useQuery } from '@apollo/client';
import { SAVED_SEARCHES_QUERY } from '@general/graphql/saved-searches.graph';
import { getAlgoliaIndexNameGlobal } from '@general/helpers/search.helpers';
import useAlgoliaClient from '@hooks/search/useAlgoliaClient';
import useViewer from '@hooks/useViewer';
import {
    generateFiltersFromSavedSearch,
    generateOptionalFiltersFromSavedSearch,
} from './algoliaFilters/useMemberMatchFilters';
import React, { useState, useEffect } from 'react';

export const NEW_CONNECTIONS_MAX_DAYS = 7;
const JOIN_DATE_REPLICA_SUFFIX = '_join_date_desc';
const SAVED_SEARCH_CREATED_AT_REPLICA_SUFFIX = '_search_created_desc';

function generateNewOnlyFilter(search) {
    const now = new Date();
    const daysAgo = now.setDate(now.getDate() - NEW_CONNECTIONS_MAX_DAYS).valueOf() / 1000;
    if (search.primaryInterest === 'CONNECTIONS') {
        return `joinDate > ${daysAgo}`;
    }

    return `savedSearch.createdAtTimestamp > ${daysAgo}`;
}

function useSavedSearchesWithMatches() {
    const { viewer } = useViewer();
    const { loading, data: { savedSearches } = [] } = useQuery(SAVED_SEARCHES_QUERY);
    const { searchClient } = useAlgoliaClient();
    const [savedSearchesWithMatches, setSavedSearchesWithMatches] = useState([]);

    useEffect(() => {
        if (!searchClient) {
            console.log('searchClient not set');
            return;
        }

        if (!savedSearches || savedSearches.length === 0) {
            console.log('no saved searches');
            return;
        }

        if (!viewer) {
            console.log('no viewer');
            return;
        }

        (async () => {
            const searchesWithMatches = await Promise.all(
                savedSearches.map(async search => {
                    let indexName = getAlgoliaIndexNameGlobal('saved_searchable_members');
                    indexName +=
                        search.primaryInterest === 'CONNECTIONS'
                            ? JOIN_DATE_REPLICA_SUFFIX
                            : SAVED_SEARCH_CREATED_AT_REPLICA_SUFFIX;
                    const filters = generateFiltersFromSavedSearch(search, [generateNewOnlyFilter(search)], viewer);
                    const optionalFilters = generateOptionalFiltersFromSavedSearch(search);

                    try {
                        const { results } = await searchClient.search([
                            {
                                indexName,
                                params: { filters, optionalFilters },
                            },
                        ]);

                        return {
                            search,
                            matches: results,
                        };
                    } catch (e) {
                        console.error('Error fetching matches for saved search', e);
                        return {
                            search,
                            matches: []
                        };
                    }
                }),
            );
            setSavedSearchesWithMatches(searchesWithMatches);
        })();
    }, [savedSearches, searchClient, viewer]);

    return { loading, savedSearchesWithMatches };
}

export default useSavedSearchesWithMatches;
